import * as Sentry from '@sentry/browser';
import { environment } from '../../../environments/environment';

/**
 * TR-22 (A): Sentry sólo para ERRORES (sin performance tracing, para no gastar
 * la cuota gratis, que es por organización y se comparte con otros proyectos).
 *
 * El DSN viene de environment.sentryDsn. Vacío = desactivado: no se inicializa
 * nada y capturarError() no hace nada.
 */

// Tope de eventos por carga de página: protege la cuota si un error se repite en bucle.
const MAX_EVENTOS_POR_SESION = 25;
let eventosEnviados = 0;
let activo = false;
let ultimaClave = '';
let ultimoTs = 0;

function usuarioActual(): string {
    try {
        const uid = window.localStorage.getItem('usuario.uid');
        return uid && uid !== 'null' ? uid : null;
    } catch (e) {
        return null;
    }
}

function sinQuery(url: any): any {
    if (typeof url !== 'string') {
        return url;
    }
    const i = url.indexOf('?');
    return i >= 0 ? url.substring(0, i) : url;
}

function claveEvento(event: any): string {
    try {
        const ex = event.exception && event.exception.values && event.exception.values[0];
        return ex ? String(ex.type) + '|' + String(ex.value) : String(event.message);
    } catch (e) {
        return '';
    }
}

export function iniciarSentry(): void {
    const dsn = (environment as any).sentryDsn;
    const host = (environment as any).host;
    // Sólo en producción: el build de producción usa environment.ts (el mismo que en local).
    if (!dsn || activo || !host || typeof window === 'undefined' || window.location.hostname !== host) {
        return;
    }
    try {
        Sentry.init({
            dsn: dsn,
            environment: window.location.hostname,
            attachStacktrace: true,
            // Los breadcrumbs de consola y las URLs con query pueden traer datos de clientes.
            beforeBreadcrumb: function (breadcrumb: any) {
                if (breadcrumb.category === 'console') {
                    return null;
                }
                if (breadcrumb.data && breadcrumb.data.url) {
                    breadcrumb.data.url = sinQuery(breadcrumb.data.url);
                }
                return breadcrumb;
            },
            // El ErrorHandler de Angular ya captura las promesas rechazadas (zone.js); aquí sólo
            // se deja window.onerror para errores fuera de Angular, sin duplicar eventos.
            integrations: function (defaults: any[]) {
                return defaults.map(function (i: any) {
                    return i && i.name === 'GlobalHandlers'
                        ? new Sentry.Integrations.GlobalHandlers({ onerror: true, onunhandledrejection: false })
                        : i;
                });
            },
            beforeSend: function (event: any) {
                if (eventosEnviados >= MAX_EVENTOS_POR_SESION) {
                    return null;
                }
                // Mismo error repetido en < 5 s: se descarta (protege la cuota compartida).
                const clave = claveEvento(event);
                const ahora = Date.now();
                if (clave === ultimaClave && ahora - ultimoTs < 5000) {
                    return null;
                }
                ultimaClave = clave;
                ultimoTs = ahora;
                eventosEnviados++;
                // La URL de la página puede traer ids/filtros; las cabeceras no hacen falta.
                if (event.request) {
                    if (event.request.url) {
                        event.request.url = sinQuery(String(event.request.url).split('#')[0]);
                    }
                    delete event.request.headers;
                    delete event.request.query_string;
                    delete event.request.cookies;
                }
                // Sólo uid como contexto de usuario (sin correo ni nombre).
                const uid = usuarioActual();
                event.user = uid ? { id: uid } : undefined;
                return event;
            }
        });
        activo = true;
    } catch (e) {
        activo = false;
    }
}

export function establecerVersionSentry(version: string): void {
    if (!activo) {
        return;
    }
    try {
        Sentry.configureScope(function (scope: any) {
            scope.setTag('version', version);
        });
    } catch (e) { }
}

export function capturarError(error: any): void {
    if (!activo) {
        return;
    }
    try {
        // Angular envuelve las excepciones de promesas en { rejection: err }.
        const original = error && error.originalError ? error.originalError : (error && error.rejection ? error.rejection : error);
        Sentry.captureException(original);
    } catch (e) { }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/monitoring/sentry.ts